/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { whatsappAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'

const TICKS = { SENT: '✓', DELIVERED: '✓✓', READ: '✓✓ read', FAILED: 'not sent' }

/** A record's WhatsApp conversation, and the composer (free text in the 24-hour window, else a template). */
const WhatsAppDialog = ({ record, onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const key = [tenantId, 'whatsapp', 'thread', record.id]
  const thread = useQuery({ queryKey: key, queryFn: () => whatsappAPI.thread(record.id), refetchInterval: 15_000 })
  const templates = useQuery({ queryKey: [tenantId, 'whatsapp', 'templates'], queryFn: whatsappAPI.templates, enabled: thread.data && !thread.data.windowOpen })
  const [text, setText] = useState('')
  const [template, setTemplate] = useState('')
  const [params, setParams] = useState([])
  const t = (templates.data || []).find((x) => x.name === template)
  const send = useMutation({
    mutationFn: () => whatsappAPI.send(record.id, thread.data.windowOpen ? { text: text.trim() } : { template, language: t?.language, params }),
    onSuccess: (m) => {
      queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({ queryKey: [tenantId, 'records', 'activity'] })
      if (m.status === 'FAILED') notify(`Not sent: ${m.error}`, 'error')
      setText(''); setTemplate(''); setParams([])
    },
  })
  const th = thread.data
  const messages = [...(th?.messages || [])].reverse()
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>WhatsApp{th?.number ? ` · +${th.number}` : ''}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.default', minHeight: 240, maxHeight: 420 }}>
        {thread.error && <Alert severity="error">{getErrorMessage(thread.error)}</Alert>}
        {th && !th.number && <Alert severity="info">This record has no mobile number WhatsApp can use.</Alert>}
        {messages.length === 0 && th?.number && <Typography color="text.secondary" variant="body2">No messages yet.</Typography>}
        <Stack spacing={1}>
          {messages.map((m) => (
            <Box key={m.id} sx={{ alignSelf: m.direction === 'OUT' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
              <Box sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: m.direction === 'OUT' ? 'primary.main' : 'background.paper',
                color: m.direction === 'OUT' ? 'primary.contrastText' : 'text.primary', border: m.direction === 'OUT' ? 0 : 1, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</Typography>
              </Box>
              <Typography variant="caption" color={m.status === 'FAILED' ? 'error' : 'text.secondary'} sx={{ display: 'block', textAlign: m.direction === 'OUT' ? 'right' : 'left' }}>
                {format(new Date(m.createdOn), 'd MMM HH:mm')}{m.direction === 'OUT' ? ` · ${TICKS[m.status] || ''}` : ''}{m.status === 'FAILED' && m.error ? ` · ${m.error}` : ''}
              </Typography>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      {th?.number && (
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2 }}>
          {send.error && <Alert severity="error">{getErrorMessage(send.error)}</Alert>}
          {th.windowOpen ? (
            <TextField multiline minRows={2} maxRows={6} placeholder="Message" value={text} onChange={(e) => setText(e.target.value)} inputProps={{ maxLength: 4096 }}
              helperText={`They wrote recently: free text until ${format(new Date(th.windowClosesOn), 'd MMM HH:mm')}`} />
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">They haven’t written in 24 hours, so WhatsApp only allows an approved template.</Typography>
              <TextField select size="small" label="Template" value={template} onChange={(e) => { setTemplate(e.target.value); setParams([]) }}>
                {(templates.data || []).map((x) => <MenuItem key={`${x.name}-${x.language}`} value={x.name}>{x.name} ({x.language})</MenuItem>)}
              </TextField>
              {t && <Typography variant="body2" sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>{t.body}</Typography>}
              {t && Array.from({ length: t.variables }, (_, i) => (
                <TextField key={i} size="small" label={`{{${i + 1}}}`} value={params[i] || ''} placeholder="Text, or {{field}} from the record"
                  onChange={(e) => { const p = [...params]; p[i] = e.target.value; setParams(p) }} />
              ))}
            </>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" disabled={send.isPending || (th.windowOpen ? !text.trim() : !t || params.filter((p) => p?.trim()).length < t.variables)}
              onClick={() => send.mutate()}>Send</Button>
          </Stack>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default WhatsAppDialog
